import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import NeuralMeshBackground from "@/components/NeuralMeshBackground";

// ─────────────────────────────────────────────────────────────────────
// Architect Directive 2026-07-02 · Monolithic Door REVERSED.
// nextxus.online is now the live production Federation home. Preview
// (truth-gate-core.preview.emergentagent.com) renders normally too.
// The hostname-guard redirect that previously bounced visitors to
// next-xus.com was the root cause of the "Loading..." blank state and
// has been removed. Backend redirect middleware is also disabled in
// /app/backend/server.py — see comment there.
// ─────────────────────────────────────────────────────────────────────

import Sidebar from "@/components/cathedral/Sidebar";
import TopBar from "@/components/cathedral/TopBar";
import ArchitectConsole from "@/pages/ArchitectConsole";
import HomePage from "@/pages/HomePage";
import ExecutePanel from "@/pages/ExecutePanel";
import LedgerExplorer from "@/pages/LedgerExplorer";
import Deliberations from "@/pages/Deliberations";
import VesselsPage from "@/pages/Vessels";
import LibraryPage from "@/pages/Library";
import NodesPanel from "@/pages/NodesPanel";
import DirectivesPage from "@/pages/Directives";
import ArchitectSuite from "@/pages/ArchitectSuite";
import SettingsPage from "@/pages/Settings";
import CockpitPage from "@/pages/Cockpit";
import ChatRoom from "@/pages/ChatRoom";
import PromptFederation from "@/pages/PromptFederation";
import RedeemPage from "@/pages/RedeemPage";
import FeedPage from "@/pages/FeedPage";
import TruthVerifierPeerPage from "@/pages/TruthVerifierPeerPage";
import PressKit from "@/pages/PressKit";
import BoardPage from "@/pages/BoardPage";
import ConsultPage from "@/pages/ConsultPage";
import BackerPage from "@/pages/BackerPage";
import SharePage from "@/pages/SharePage";
import EmbedCardsPage from "@/pages/EmbedCardsPage";
import BooksPage from "@/pages/BooksPage";
import FederationStatusPage from "@/pages/FederationStatusPage";
import CodexChamber from "@/pages/CodexChamber";
import MediaEmbeds from "@/pages/MediaEmbeds";
import ResearchLab from "@/pages/ResearchLab";
import DailyVoiceBlog from "@/pages/DailyVoiceBlog";
import CouncilPage from "@/pages/CouncilPage";
import RadioPage from "@/pages/RadioPage";
import RadioStudio from "@/pages/RadioStudio";
import EmbedRadio from "@/pages/EmbedRadio";
import BroadcastArchive from "@/pages/BroadcastArchive";
import { AuthCallback } from "@/components/cathedral/MemberAuth";
import MemberLogin from "@/pages/MemberLogin";
import HumanCodexPage from "@/pages/HumanCodexPage";
import LabsPage from "@/pages/LabsPage";
import VideoPage from "@/pages/VideoPage";
import NewsroomPicker from "@/components/cathedral/NewsroomPicker";
import FederationThinBar from "@/components/cathedral/FederationThinBar";
import AIChatPopup from "@/components/cathedral/AIChatPopup";
import CathedralBackdrop from "@/components/cathedral/CathedralBackdrop";
import FederationClerk from "@/components/cathedral/FederationClerk";
import FederationFooter from "@/components/cathedral/FederationFooter";
import FederationHub from "@/components/cathedral/FederationHub";
import RexInvitation from "@/components/cathedral/RexInvitation";
import AutoReadingFace from "@/components/cathedral/AutoReadingFace";
import KeyboardGiftEngine from "@/components/cathedral/KeyboardGiftEngine";
import { api } from "@/lib/api";

const TRAVELER_KEY = "nx_traveler_id";

function readSatelliteHost() {
  try {
    const u = new URL(window.location.href);
    return (u.searchParams.get("host") || "embed").slice(0, 32);
  } catch { return "embed"; }
}

function readHeartbeatOptOut() {
  try {
    const u = new URL(window.location.href);
    return u.searchParams.get("heartbeat") === "off";
  } catch { return false; }
}

function ensureTravelerId() {
  try {
    let id = localStorage.getItem(TRAVELER_KEY);
    if (!id) {
      id = "tr_" + Math.random().toString(36).slice(2, 14);
      localStorage.setItem(TRAVELER_KEY, id);
    }
    return id;
  } catch { return "tr_anon"; }
}

/**
 * EmbedShell — what satellite apps see when they iframe the Cockpit.
 * Renders ONLY the Universal Cockpit at the bottom — no sidebar, no topbar,
 * no Architect surface. Designed to drop in via:
 *   <iframe src=".../embed/cockpit?host=aria" />
 *
 * Pings /api/satellite/heartbeat on mount so the Architect Console can see
 * which chapels are live. Opt-out: append `?heartbeat=off`.
 */
function EmbedShell() {
  React.useEffect(() => {
    if (readHeartbeatOptOut()) return;
    const host = readSatelliteHost();
    // Once per browser session per host — prevents StrictMode double-fire
    // and prevents repeated pings on hot-reload during dev.
    try {
      const key = `nx_hb_${host}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { /* sessionStorage may be denied in iframe sandboxes */ }
    try {
      api.satelliteHeartbeat({
        host,
        traveler_id: ensureTravelerId(),
        referrer: document.referrer || "",
        user_agent: navigator.userAgent || "",
      }).catch(() => { /* best-effort */ });
    } catch { /* best-effort */ }
  }, []);
  return (
    <div
      className="fixed inset-x-0 bottom-0 bg-transparent text-[var(--text)] flex flex-col justify-end max-h-[360px]"
      data-testid="embed-shell"
    >
      <div className="w-full">
        <FederationThinBar embedded />
        <AIChatPopup />
      </div>
    </div>
  );
}

/**
 * EmbedNewsroom — what satellites see when they iframe the Newsroom.
 * Auto-refreshing 12-item federation activity feed. No chrome.
 */
function EmbedNewsroom() {
  return (
    <div
      className="fixed inset-0 bg-[var(--void)] text-[var(--text)] cathedral-nave overflow-auto p-4"
      data-testid="embed-newsroom"
    >
      <NewsroomPicker limit={12} />
    </div>
  );
}

function BackToThrone() {
  return (
    <div className="text-center py-6" data-testid="back-to-throne-bottom">
      <a
        href="https://nextxus.tech"
        className="font-mono uppercase inline-flex items-center justify-center"
        style={{
          fontSize: 16,
          fontWeight: 700,
          letterSpacing: "0.18em",
          color: "#FFD700",
          border: "1px solid rgba(255,215,0,0.55)",
          borderRadius: 999,
          padding: "10px 26px",
          minHeight: 44,
          textDecoration: "none",
          textShadow: "0 0 12px rgba(0,255,204,0.55)",
          boxShadow: "0 0 18px rgba(255,215,0,0.22)",
          transition: "box-shadow 0.25s ease, color 0.25s ease",
        }}
      >
        ← Back to Throne
      </a>
    </div>
  );
}

function Shell() {
  // Legacy querystring embed (?embed=1) — kept for backward compatibility.
  const params = new URLSearchParams(window.location.search);
  if (params.get("embed") === "1") return <EmbedShell />;
  return (
    <div className="min-h-screen flex bg-[var(--void)] text-[var(--text)] cathedral-nave relative pb-[64px]">
      <CathedralBackdrop circuitOpacity={0.55} />
      <Sidebar />
      <main className="flex-1 min-w-0 flex flex-col relative z-10">
        <TopBar />
        <div className="flex-1 relative">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<MemberLogin />} />
            <Route path="/console" element={<ArchitectConsole />} />
            <Route path="/execute" element={<ExecutePanel />} />
            <Route path="/ledger" element={<LedgerExplorer />} />
            <Route path="/deliberations" element={<Deliberations />} />
            <Route path="/vessels" element={<VesselsPage />} />
            <Route path="/library" element={<LibraryPage />} />
            <Route path="/nodes" element={<NodesPanel />} />
            <Route path="/directives" element={<DirectivesPage />} />
            <Route path="/architect" element={<ArchitectSuite />} />
            <Route path="/cockpit" element={<CockpitPage />} />
            <Route path="/chat" element={<ChatRoom />} />
            <Route path="/prompt-federation" element={<PromptFederation />} />
            <Route path="/redeem" element={<RedeemPage />} />
            <Route path="/feed" element={<FeedPage />} />
            <Route path="/press-kit" element={<PressKit />} />
            <Route path="/board" element={<BoardPage />} />
            <Route path="/consult" element={<ConsultPage />} />
            <Route path="/backer" element={<BackerPage />} />
            <Route path="/founder" element={<BackerPage />} />
            <Route path="/sponsor" element={<BackerPage />} />
            <Route path="/share" element={<SharePage />} />
            <Route path="/embed-cards" element={<EmbedCardsPage />} />
            <Route path="/embeds" element={<EmbedCardsPage />} />
            <Route path="/books" element={<BooksPage />} />
            <Route path="/stories" element={<BooksPage />} />
            <Route path="/codex" element={<CodexChamber />} />
            <Route path="/the-unverified-echo" element={<CodexChamber />} />
            <Route path="/media" element={<MediaEmbeds />} />
            <Route path="/embeds-hub" element={<MediaEmbeds />} />
            <Route path="/affiliate" element={<MediaEmbeds />} />
            <Route path="/research" element={<ResearchLab />} />
            <Route path="/deep-ai" element={<ResearchLab />} />
            <Route path="/creativity" element={<ResearchLab />} />
            <Route path="/blog" element={<DailyVoiceBlog />} />
            <Route path="/daily" element={<DailyVoiceBlog />} />
            <Route path="/council" element={<CouncilPage />} />
            <Route path="/radio" element={<RadioPage />} />
            <Route path="/broadcasts" element={<BroadcastArchive />} />
            <Route path="/radio-studio" element={<RadioStudio />} />
            <Route path="/humancodex" element={<HumanCodexPage />} />
            <Route path="/labs" element={<LabsPage />} />
            <Route path="/video" element={<VideoPage />} />
            <Route path="/status" element={<FederationStatusPage />} />
            <Route path="/federation-status" element={<FederationStatusPage />} />
            <Route path="/peers/truth-verifier" element={<TruthVerifierPeerPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </div>
        <FederationHub />
        <BackToThrone />
        <FederationFooter />
      </main>
      <FederationThinBar />
      <AIChatPopup />
      <FederationClerk />
      <AutoReadingFace />
      <KeyboardGiftEngine />
      <RexInvitation />
    </div>
  );
}

function RootRoutes() {
  // Google OAuth return: session_id arrives in the URL fragment — must be
  // handled synchronously during render, before any route mounts.
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      {/* Iframe-ready cockpit. Path-based so external sites can drop in
          <iframe src="…/embed/cockpit?host=aria"> with zero React knowledge. */}
      <Route path="/embed" element={<EmbedShell />} />
      <Route path="/embed/cockpit" element={<EmbedShell />} />
      <Route path="/embed/newsroom" element={<EmbedNewsroom />} />
      <Route path="/embed/radio" element={<EmbedRadio />} />
      {/* Standalone /video · P0 recursion-safety route (must precede `*`).
          The Federation Video Hub iframe used to target /video with no
          matching route → wildcard rendered full Shell → HomePage iframe
          re-loaded /video → infinite recursion → blank white screen. */}
      <Route path="/video" element={<VideoPage />} />
      {/* Everything else uses the full Cathedral shell. */}
      <Route path="*" element={<Shell />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <NeuralMeshBackground />
      <RootRoutes />
    </BrowserRouter>
  );
}
